import React, { useState } from 'react';
import { InputText } from 'primereact/inputtext';
import { CustomExpander } from '../CustomExpander';
import { FilterCheckBoxItem } from './filter-check-box-item';

interface FilterExpanderWithListOfCheckboxesProps<
  T,
  K extends string | number = string,
> {
  title: string;
  items: T[];
  selectedItems: K[];
  onItemToggle: (item: K) => void;
  onSelectionSet?: (selectedItems: K[]) => void;
  getItemLabel: (item: T) => string;
  getItemKey: (item: T) => K;
  defaultExpanded?: boolean;
  searchPlaceholder?: string;
  allowSelectAll?: boolean;
  allowClear?: boolean;
}

const styles = {
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'flex-start',
    gap: '12px',
  },
  headerTitle: {
    flex: 1,
    minWidth: 0,
  },
  headerActions: {
    display: 'flex',
    gap: '8px',
    flexShrink: 0,
  },
  section: {
    padding: '4px 0',
  },
  search: {
    width: '100%',
    marginBottom: '12px',
    direction: 'rtl' as const,
    backgroundColor: '#f8f9fa',
    border: '1px solid #e9ecef',
    borderRadius: '6px',
    padding: '8px 12px',
    fontSize: '0.95rem',
    transition: 'all 0.2s ease',
    outline: 'none',
  },
  checkboxList: {
    maxHeight: '200px',
    overflowY: 'auto' as const,
    borderRadius: '6px',
    padding: '4px',
    backgroundColor: '#f8f9fa',
    border: '1px solid #e9ecef',
  },
};

export function FilterExpanderWithListOfCheckboxes<
  T,
  K extends string | number = string,
>({
  title,
  items,
  selectedItems,
  onItemToggle,
  onSelectionSet,
  getItemLabel,
  getItemKey,
  defaultExpanded = false,
  searchPlaceholder = 'חפש...',
  allowSelectAll = false,
  allowClear = false,
}: FilterExpanderWithListOfCheckboxesProps<T, K>) {
  const [searchText, setSearchText] = useState('');

  const filteredItems = items.filter((item) =>
    getItemLabel(item).toLowerCase().includes(searchText.toLowerCase()),
  );

  const handleSelectAll = () => {
    if (onSelectionSet) {
      onSelectionSet(items.map((item) => getItemKey(item)));
      return;
    }
    items.forEach((item) => {
      const key = getItemKey(item);
      if (!selectedItems.includes(key)) {
        onItemToggle(key);
      }
    });
  };

  const handleClearAll = () => {
    if (onSelectionSet) {
      onSelectionSet([]);
      return;
    }
    // `onItemToggle` is expected to be a toggle, so calling it for all currently
    // selected keys will clear the selection.
    selectedItems.forEach((key) => onItemToggle(key));
  };

  const preventHeaderToggle = (e: React.MouseEvent) => {
    // This component is rendered inside `CustomExpander`'s clickable header.
    // Prevent button clicks from toggling the expander open/close.
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <CustomExpander
      badge={selectedItems.length}
      defaultExpanded={defaultExpanded}
    >
      <div style={styles.headerRow}>
        <div style={styles.headerTitle}>{title}</div>
        <div style={styles.headerActions}>
          {allowSelectAll && (
            <i
              className="pi pi-check-square"
              style={{
                cursor: 'pointer',
                fontSize: '16px',
                color: 'var(--text-color-secondary)',
              }}
              title="בחר הכל"
              data-pr-tooltip="בחר הכל"
              onClick={(e) => {
                preventHeaderToggle(e);
                handleSelectAll();
              }}
            />
          )}
          {allowClear && (
            <i
              className="pi pi-filter-slash"
              style={{
                cursor: 'pointer',
                fontSize: '16px',
                color: 'var(--text-color-secondary)',
              }}
              title="נקה"
              data-pr-tooltip="נקה"
              onClick={(e) => {
                preventHeaderToggle(e);
                handleClearAll();
              }}
            />
          )}
        </div>
      </div>
      <div style={styles.section}>
        <InputText
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          placeholder={searchPlaceholder}
          style={styles.search}
        />
        <div style={styles.checkboxList}>
          {filteredItems.map((item) => (
            <FilterCheckBoxItem
              key={getItemKey(item).toString()}
              label={getItemLabel(item)}
              isChecked={selectedItems.includes(getItemKey(item))}
              onChange={() => onItemToggle(getItemKey(item))}
            />
          ))}
        </div>
      </div>
    </CustomExpander>
  );
}
